"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import { createPortal } from "react-dom";
import { useAuth } from "@/hooks/useAuth";
import { apiFetch } from "@/lib/api/client";
import { DAILY_MODES, type DailyCounter, type DailyMode } from "@/lib/daily-missions";
import { presentRewardLabel, type PresentReward } from "@/lib/presents";
import type { DailyMissionView, DailyState } from "@/lib/daily-missions-server";
import Glyph from "@/components/ui/Glyph";
import { useGlyphMode } from "@/lib/glyph-mode";
import { announceDailyMode } from "@/lib/nav-state";
import LibrarySeal from "@/components/ui/LibrarySeal";
import PhraseText from "@/components/book/PhraseText";
import { useUiTexts } from "@/lib/ui-texts-client";

const MODE_ICON: Record<DailyMode, string> = { collect: "✨", grow: "🌱", gather: "💎" };

function useDaily() {
  const { user } = useAuth();
  const [state, setState] = useState<DailyState | null>(null);
  const load = useCallback(async () => {
    if (!user) return;
    const token = await user.getIdToken();
    const r = await apiFetch<DailyState>("/api/daily", token).catch(() => null);
    if (r) setState(r);
  }, [user]);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    user
      .getIdToken()
      .then((token) => apiFetch<DailyState>("/api/daily", token))
      .then((r) => alive && setState(r))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [user]);
  return { user, state, setState, load };
}

/**
 * ホームの小さな欄。読み方と、その日のミッションを短い名前で1行ずつ（印・名前・細いゲージ・数）。押すとクエスト画面へ。
 * その日の様子はホームが読んで渡す（司書の声かけにも使うため）
 */
export function DailyMissionsCompact({ state, onOpen }: { state: DailyState | null; onOpen: () => void }) {
  const { t } = useUiTexts();
  if (!state) return null;
  return (
    <button
      onClick={onOpen}
      className="w-full space-y-1.5 rounded-xl border border-white/15 bg-[var(--background)]/75 p-2.5 text-left text-[var(--foreground)] backdrop-blur-sm transition-all active:scale-[0.98]"
    >
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-semibold opacity-90">{t("quests.daily.compact.title")}</p>
      </div>
      {state.mode ? (
        <>
          <p className="truncate text-[11px]">
            <span className="mr-1"><Glyph e={MODE_ICON[state.mode]} /></span>
            {state.modes[state.mode].label}
          </p>
          <ul className="space-y-1">
            {state.missions.map((m) => (
              <li key={m.id} className="flex items-center gap-1.5 text-[11px]">
                <span className={`w-4 shrink-0 text-center ${m.claimed ? "opacity-40" : ""}`} aria-hidden="true"><Glyph e={COUNTER_ICON[m.counter]} /></span>
                <span className={`min-w-0 flex-1 truncate ${m.claimed ? "opacity-40" : ""}`}>{m.shortText}</span>
                {m.claimed ? (
                  // 受け取り済みは金の星（小さいので輪は付かない）。行は薄くしても、印は薄くしない
                  <LibrarySeal text={t("quests.daily.compact.claimedSeal")} label={t("quests.daily.compact.claimedAria")} emblem="star" size={15} />
                ) : m.done ? (
                  <span className="shrink-0 text-[10px] font-bold text-amber-300">✓</span>
                ) : (
                  <>
                    <span className="h-1 w-8 shrink-0 overflow-hidden rounded-full bg-white/15">
                      <span
                        className="block h-1 rounded-full bg-violet-400"
                        style={{ width: `${Math.round((m.progress / m.target) * 100)}%` }}
                      />
                    </span>
                    <span className="w-7 shrink-0 text-right text-[10px] opacity-70">
                      {m.progress}/{m.target}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
          {state.buffLabels.length > 0 && (
            <p className="truncate text-[10px] text-emerald-300">{state.buffLabels.join("・")}</p>
          )}
        </>
      ) : (
        <p className="text-[11px] text-violet-300">{t("quests.daily.compact.choose")}</p>
      )}
    </button>
  );
}

/** 数えるものの印（ミッションの行の左） */
const COUNTER_ICON: Record<DailyCounter, string> = {
  read: "📖",
  read_party_genre: "🤝",
  read_unowned_genre: "🗝️",
  novel_gacha: "🎲",
  spirit_gacha: "🔮",
  spirit_exp: "🌱",
  feed: "🧺",
  materials: "💎",
  adventure: "🧭",
  gacha_read: "📕",
  encounter: "✨",
};

/** 報酬1つを絵と数で（ミッションの行の右） */
function RewardChip({ reward }: { reward: PresentReward }) {
  const { t } = useUiTexts();
  const glyphMode = useGlyphMode();
  const imageUrl = reward.type === "material" || reward.type === "spirit" ? reward.imageUrl : "";
  const icon =
    reward.type === "spirit_exp" ? null
      : reward.type === "spirit" ? "✨"
        : reward.type === "novel" ? "📖"
          : "💎";
  const amount =
    reward.type === "spirit_exp" ? `+${reward.amount}`
      : reward.type === "material" || reward.type === "material_random" ? `×${reward.quantity}`
        : "×1";
  return (
    <span className="flex w-11 flex-col items-center rounded-[2px] border border-[#9a4a3a55] bg-[#fff8e8]/50 px-1 py-1" title={presentRewardLabel(reward)}>
      {imageUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={imageUrl} alt="" className="h-6 w-6 object-contain" />
      ) : icon ? (
        // アイコンの場所は 24px 角に固定する（アイコンが小さい・出さない設定でも縮まず、「?」が下の数に重ならないように・10/12）
        <span className="relative flex h-6 w-6 items-center justify-center text-lg leading-none">
          <Glyph e={icon} />
          {/* 素材を名指ししない報酬（ランダム）。アイコンを出さない設定なら、「?」をアイコン代わりに真ん中へ */}
          {reward.type === "material_random" && (
            glyphMode === "none"
              ? <span className="text-sm font-bold text-[#8a4a35]">?</span>
              : <span className="absolute -right-0.5 top-0 text-[9px] font-bold leading-none text-[#8a4a35]">?</span>
          )}
        </span>
      ) : (
        <span className="text-[10px] font-bold leading-6 text-[#2f6b45]">{t("quests.daily.reward.exp")}</span>
      )}
      <span className="text-[10px] font-bold leading-tight">{amount}</span>
    </span>
  );
}

/**
 * 選んだ読み方の今日だけの効果（緑の囲い）。1行目に切れるまでの残り時間（分まで・1分ごとに数え直す）、その下に効果を1つずつ1行で（10/12 本人の指示）。
 * 残りが0になったら、新しい日の状態を読み直す
 */
/** 今日だけの効果が切れるまでの残り（分まで・30秒ごとに数え直す）の文。0になったら onExpire（新しい日の状態を読み直す） */
function useRemainingText(nextResetAt: number | null, onExpire: () => void): string {
  const { t } = useUiTexts();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);
  // 区切りの時刻がまだ分からない（今日の読み方を読み込む前）ときは、切れたことにしない・文も出さない
  const left = nextResetAt === null ? Infinity : Math.max(0, nextResetAt - now);
  useEffect(() => {
    if (left > 0) return;
    const id = window.setTimeout(onExpire, 1500);
    return () => window.clearTimeout(id);
  }, [left, onExpire]);
  if (nextResetAt === null) return "";
  // 切れるまでの残りは分を切り上げる（「あと0分」と出たまま効いている時間をつくらない）
  const minutes = Math.ceil(left / 60_000);
  return minutes >= 60
    ? t("quests.daily.remaining", { 時間: Math.floor(minutes / 60), 分: minutes % 60 })
    : t("quests.daily.remainingMinutes", { 分: minutes });
}

/** 栞の穴から上へ出て右へ垂れる組紐と房（確かめる栞・書庫から見る栞） */
function BookmarkCord() {
  return (
    <svg aria-hidden="true" className="library-bookmark-cord" viewBox="0 0 54 92">
      <path d="M4 58 C2 40 6 20 18 12 C30 4 42 12 40 26 C38 40 34 50 36 62" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <rect x="31.5" y="61" width="9" height="6" rx="2" fill="currentColor" />
      <path d="M32 67 H40 L43.5 88 H28.5 Z" fill="currentColor" />
      <path d="M31 72 V87 M34 72 V88 M37 72 V88 M40 72 V87" stroke="#0000004d" strokeWidth="1" />
    </svg>
  );
}

function BuffBox({ labels, resetHour, nextResetAt, onExpire }: { labels: string[]; resetHour: string; nextResetAt: number; onExpire: () => void }) {
  const { t } = useUiTexts();
  const remaining = useRemainingText(nextResetAt, onExpire);
  return (
    // 朱の点線の囲いに、1行目は切れるまでの残り（小さく）、下に効果を朱の字で（10/18 厚紙のカード）
    <div className="border border-dashed border-[#b5372c66] px-2.5 py-1.5">
      <p className="text-[11px] text-[#6b553a]">{t("quests.daily.buff", { 時刻: resetHour, 残り: remaining })}</p>
      <ul className="text-[13.5px] font-semibold text-[#b5372c]">
        {labels.map((label, i) => (
          <li key={i}>{t("quests.daily.buffItem", { 効果: label })}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * ミッションの1行: 左に印刷のチェック欄（達成したら朱のレ点）、真ん中に名前と進み（印刷の枠に鉛筆の斜線）、右に報酬の絵と数。
 * 達成した報酬はその場でプレゼントボックスに届く（10/22 に行の「受け取る」を外した）。行は厚紙のカードの中に細い罫で区切って並べる（10/18。前は読み方の色の付箋を1枚ずつ）
 */
function MissionRow({
  mission,
  pityRemaining,
}: {
  mission: DailyMissionView;
  /** 出会いのミッションに添える、その日の確定まであと何冊（もう出会っていれば null） */
  pityRemaining: number | null;
}) {
  const { t } = useUiTexts();
  const pct = Math.round((mission.progress / mission.target) * 100);
  return (
    <div className="relative space-y-2 border-t border-[#9a4a3a33] py-2.5 first:border-t-0 first:pt-0">
      {/* 受け取り済みは中身を薄くし、金の箔押しの紋章を報酬の上に押す（印は薄くしない） */}
      {mission.claimed && (
        <LibrarySeal text={t("quests.daily.mission.claimedSeal")} caption={t("quests.daily.mission.claimedSeal")} label={t("quests.daily.mission.claimed")} emblem="star" size={52} className="pointer-events-none absolute right-0 top-1/2 z-10 -mt-[26px]" />
      )}
      <div className={`flex items-start gap-2 ${mission.claimed ? "opacity-50" : ""}`}>
        <span className="library-check mt-[5px]" data-checked={mission.done ? "" : undefined} aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-[13px] leading-snug">{mission.text}</p>
          {mission.claimed ? (
            <p className="text-[11px] opacity-80">{t("quests.daily.mission.claimed")}</p>
          ) : (
            <div className="flex items-center gap-2">
              <div className="library-gauge min-w-0 flex-1" data-done={mission.done ? "" : undefined}>
                <span style={{ width: `${pct}%` }} />
              </div>
              <span className="shrink-0 text-[11px] tabular-nums">{mission.progress} / {mission.target}</span>
            </div>
          )}
        </div>
        {/* 受け取り済みは報酬の札を見えなくして、同じ場所に紋章だけを残す（幅は残して並びを変えない） */}
        <div className={`flex shrink-0 gap-1 ${mission.claimed ? "invisible" : ""}`} aria-label={t("quests.daily.mission.rewardAria", { 報酬: mission.rewardLabel })}>
          {mission.rewards.map((r, i) => (
            <RewardChip key={i} reward={r} />
          ))}
        </div>
      </div>
      {!mission.done && mission.counter === "encounter" && pityRemaining !== null && (
        <p className="pl-5 text-[11px] text-[#8a4a35]">
          {t("quests.daily.mission.pity", { 冊数: pityRemaining })}
        </p>
      )}
    </div>
  );
}

/**
 * 書庫から、決めた今日の読み方を見る栞（10/12 本人「選ぶ前に確かめた小窓と同じ栞の形で」）。
 * 読み方の色の栞に、名前・今日だけの効果（残り時間つき）・ミッション（進みと報酬。達成したものはプレゼントボックスに届いた）を並べる。
 * 下に「閉じる」と「今日の読み方の画面へ」。読み方をまだ選んでいなければ何も出さない（書庫のしおりは選ぶ画面へ移る）
 */
export function TodayReadingBookmark({ onClose }: { onClose: () => void }) {
  const { state, load } = useDaily();
  const { t } = useUiTexts();
  const mode = state?.mode ?? null;
  const remaining = useRemainingText(state?.nextResetAt ?? null, load);
  if (!state || !mode) return null;
  const resetHour = new Date(state.nextResetAt).toLocaleTimeString("ja-JP", { hour: "numeric", timeZone: "Asia/Tokyo" });
  return createPortal((
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60" onClick={onClose}>
      <div className="mx-auto flex min-h-full max-w-md items-center justify-center px-6 py-14">
        <div
          role="dialog"
          aria-label={t("shelf.dailyPanel.aria")}
          data-mode={mode}
          className="library-bookmark-dialog space-y-3"
          onClick={(e) => e.stopPropagation()}
        >
          <BookmarkCord />
          <p className="text-center text-xs opacity-70">{t("shelf.dailyPanel.eyebrow")}</p>
          <p className="-mt-2 break-keep text-center font-mincho text-lg font-bold leading-snug [text-wrap:balance]">
            <span className="mr-1.5"><Glyph e={MODE_ICON[mode]} /></span>
            {state.modes[mode].label}
          </p>
          {state.buffLabels.length > 0 && (
            <div className="rounded-lg bg-[#fffaf0]/60 px-3 py-2 text-xs text-emerald-800">
              <p className="font-semibold">{t("quests.daily.buff", { 時刻: resetHour, 残り: remaining })}</p>
              <ul className="space-y-0.5 break-keep [text-wrap:pretty]">
                {state.buffLabels.map((label, i) => (
                  <li key={i}>{t("quests.daily.buffItem", { 効果: label })}</li>
                ))}
              </ul>
            </div>
          )}
          {/* ミッションは2段: 上に達成目標と進み、すぐ下に報酬（達成したものは届いた先） */}
          <ul className="space-y-2 rounded-lg border border-[#3a2f24]/15 bg-[#fffaf0]/50 px-3 py-2.5">
            {state.missions.map((m) => (
              <li key={m.id} className={m.claimed ? "opacity-70" : ""}>
                <p className="flex items-baseline gap-2 text-[13px] font-semibold leading-snug">
                  <span className="min-w-0 flex-1 break-keep pl-3 -indent-3 [text-wrap:pretty]">{t("quests.daily.confirm.missionItem", { ミッション: m.text })}</span>
                  <span className="shrink-0 text-[11.5px] tabular-nums">{t("shelf.dailyPanel.progress", { 進み: m.progress, 目標: m.target })}</span>
                </p>
                <p className="mt-0.5 pl-3 text-[11.5px] font-medium leading-snug text-[#3a2f24]">
                  {m.claimed ? t("shelf.dailyPanel.delivered") : t("quests.daily.confirm.missionReward", { 報酬: m.rewardLabel })}
                </p>
              </li>
            ))}
          </ul>
          <div className="flex flex-col-reverse gap-2 pt-1">
            <button onClick={onClose} className="library-bookmark-dialog-cancel">{t("shelf.dailyPanel.close")}</button>
            <Link href="/quests" className="library-bookmark-dialog-ok text-center">{t("shelf.dailyPanel.open")}</Link>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}

/**
 * クエスト画面の「今日の読み方」。モードを選ぶと、その日のミッションとバフが決まる。
 * 一度選んだら、その日（次の区切りまで）は変えられないので、選ぶ前に確かめる
 */
export default function DailyMissions() {
  const { user, state, setState, load } = useDaily();
  const { t } = useUiTexts();
  /** 確かめている読み方（「この読み方にしますか？」の板） */
  const [confirming, setConfirming] = useState<DailyMode | null>(null);
  /** 選ぶ前にタブで開いている読み方（まだ押していなければ null＝前の日の読み方） */
  const [viewing, setViewing] = useState<DailyMode | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!state) return null;

  async function choose(mode: DailyMode) {
    if (!user || busy) return;
    setBusy(mode);
    setError(null);
    try {
      const token = await user.getIdToken();
      const next = await apiFetch<DailyState>("/api/daily/mode", token, { method: "POST", body: { mode } });
      setState(next);
      setConfirming(null);
      // 下のナビの書庫の本に、選んだ色のしおりを差し込む
      if (next.mode) announceDailyMode({ mode: next.mode, label: next.modes[next.mode].label, nextResetAt: next.nextResetAt });
    } catch {
      setConfirming(null);
      setError(t("quests.daily.error.choose"));
      await load();
    } finally {
      setBusy(null);
    }
  }

  const resetHour = new Date(state.nextResetAt).toLocaleTimeString("ja-JP", { hour: "numeric", timeZone: "Asia/Tokyo" });

  // 前に出ているタブ: 選んだ後は選んだ読み方、選ぶ前は押したタブ（最初は前の日の読み方・10/18 本人の了承）
  const shown: DailyMode = state.mode ?? viewing ?? state.lastMode ?? DAILY_MODES[0];
  const shownMode = state.modes[shown];

  return (
    // 今日の読み方＝3つの読み方のタブ（耳）が付いた厚紙のカード（10/18 本人「ノートっぽい下地はいらない。3つのタブがそのまま出る画面」）。
    // 選ぶ前はタブを切り替えて見比べ、カードの下のボタンで選ぶ。選んだ後は選んだタブが前に固まり、ほかの2つは後ろで薄く押せない
    // （globals.css の .library-notebook[data-reading]。色は data-mode の --reading）
    <section className="library-notebook mx-4 mt-[max(12px,env(safe-area-inset-top))]" data-paper="kraft" data-reading="" data-mode={shown}>
      <h2 className="sr-only">{t("quests.daily.heading")}</h2>
      <div role="tablist" aria-label={t("quests.daily.heading")} className="library-notebook-tabs">
        {DAILY_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            role="tab"
            data-mode={mode}
            aria-selected={mode === shown}
            disabled={!!state.mode && mode !== state.mode}
            onClick={() => setViewing(mode)}
            className="library-notebook-tab"
          >
            <span className="truncate">{state.modes[mode].label}</span>
          </button>
        ))}
      </div>

      <div role="tabpanel" className="library-notebook-page font-mincho text-[13px] leading-relaxed">
        {/* タブを切り替えたら、紙をめくるように入れ替える */}
        <div key={shown} className="library-notebook-turn flex flex-col gap-3">
          {error && <p className="border border-red-700/30 bg-red-500/10 px-3 py-2 text-xs text-red-700">{error}</p>}

          {!state.mode ? (
            <>
              <p className="text-[11.5px] text-[#6b553a]">{t("quests.daily.intro", { 時刻: resetHour })}</p>
              {state.lastMode === shown && <p className="library-pencil-note -mb-1 self-end">{t("quests.daily.mode.last")}</p>}
              {/* 文節の切れ目でだけ折る（「読み終え / る」のように1字だけ落ちないように） */}
              {shownMode.description && <p className="break-keep text-[#5a4630] [text-wrap:pretty]"><PhraseText text={shownMode.description} /></p>}
              {/* 今日だけの効果は朱の判の右に、1つごとに1行 */}
              {shownMode.buffLabels.length > 0 && (
                <div className="flex items-start gap-2 text-[#b5372c]">
                  <span data-ui-text="" className="library-stamp mt-0.5 shrink-0">{t("quests.daily.mode.today")}</span>
                  <ul className="min-w-0 space-y-0.5 break-keep font-semibold [text-wrap:pretty]">
                    {shownMode.buffLabels.map((label, i) => (
                      <li key={i}><PhraseText text={t("quests.daily.mode.buff", { 効果: label })} /></li>
                    ))}
                  </ul>
                </div>
              )}
              {/* ミッションは印刷のチェック欄の行に。すぐ下に報酬 */}
              <ul className="space-y-2">
                {shownMode.missions.map((x, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="library-check mt-[5px]" aria-hidden="true" />
                    <span className="min-w-0">
                      <span className="block break-keep [text-wrap:pretty]"><PhraseText text={x.text} /></span>
                      <span className="block text-[11px] text-[#6b553a]">{t("quests.daily.mode.missionReward", { 報酬: x.rewardLabel })}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => setConfirming(shown)} disabled={!!busy} className="library-claim-button mt-1 h-11 w-full">
                {t("quests.daily.mode.pick")}
              </button>
            </>
          ) : (
            <>
              {state.buffLabels.length > 0 && (
                <BuffBox labels={state.buffLabels} resetHour={resetHour} nextResetAt={state.nextResetAt} onExpire={load} />
              )}
              <div className="flex flex-col">
                {state.missions.map((m) => (
                  <MissionRow
                    key={m.id}
                    mission={m}
                    pityRemaining={state.encounterPityRemaining}
                  />
                ))}
              </div>
              <p className="text-[11px] text-[#6b553a]">{t("quests.daily.mode.nextChoice", { 時刻: resetHour })}</p>
            </>
          )}
        </div>
      </div>

      {/* 選ぶ前に確かめる（一度選んだらその日は変えられない）。
          ノートの紙（z-index の重なりの箱）の中に置くと前の見出しのつまみより下に出るので、body へ出す */}
      {confirming && createPortal((
        // 画面より背の高い栞は幕ごと送る（上下に組紐と影の分の余白）
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60">
        <div className="mx-auto flex min-h-full max-w-md items-center justify-center px-6 py-14">
          {/* 大きな縦長の栞（色は選んだ付箋と同じ。globals.css の .library-bookmark-dialog） */}
          <div
            role="dialog"
            aria-label={t("quests.daily.confirm.aria")}
            data-mode={confirming}
            className="library-bookmark-dialog space-y-3"
          >
            {/* 穴から上へ出て右へ垂れる組紐と房 */}
            <BookmarkCord />
            {/* 見出しも文節の切れ目でだけ折る（「にしま / すか？」のように語の途中で折らない） */}
            <p className="break-keep text-center font-mincho text-lg font-bold leading-snug [text-wrap:balance]">
              <span className="mr-1.5"><Glyph e={MODE_ICON[confirming]} /></span>
              <PhraseText text={t("quests.daily.confirm.title", { 読み方: state.modes[confirming].label })} />
            </p>
            {/* 文節の切れ目でだけ折り、行の長さをそろえる（「〜ませ / ん」のように1文字だけ落ちないように） */}
            <p className="break-keep text-center text-xs opacity-70 [text-wrap:balance]"><PhraseText text={t("quests.daily.confirm.note", { 時刻: resetHour })} /></p>
            {state.modes[confirming].buffLabels.length > 0 && (
              <ul className="space-y-0.5 break-keep rounded-lg bg-[#fffaf0]/60 px-3 py-2 text-xs text-emerald-800 [text-wrap:pretty]">
                {state.modes[confirming].buffLabels.map((label, i) => (
                  <li key={i}><PhraseText text={t("quests.daily.confirm.buff", { 効果: label })} /></li>
                ))}
              </ul>
            )}
            {/* ミッションは2段: 上に達成目標、そのすぐ下に報酬（10/12 本人の指示） */}
            <ul className="space-y-2 rounded-lg border border-[#3a2f24]/15 bg-[#fffaf0]/50 px-3 py-2.5">
              {state.modes[confirming].missions.map((x, i) => (
                <li key={i}>
                  {/* 「・」をぶら下げ、折れた2行目を字の頭にそろえる */}
                  <p className="break-keep pl-3 -indent-3 text-[13px] font-semibold leading-snug [text-wrap:pretty]"><PhraseText text={t("quests.daily.confirm.missionItem", { ミッション: x.text })} /></p>
                  <p className="mt-0.5 pl-3 text-[11.5px] font-medium leading-snug text-[#3a2f24]">{t("quests.daily.confirm.missionReward", { 報酬: x.rewardLabel })}</p>
                </li>
              ))}
            </ul>
            {/* 栞は幅が狭いので、ボタンは縦に並べる（決めるボタンを上に）。どちらも栞になじむ短冊（globals.css の .library-bookmark-dialog-ok / -cancel） */}
            <div className="flex flex-col-reverse gap-2 pt-1">
              <button
                onClick={() => setConfirming(null)}
                disabled={!!busy}
                className="library-bookmark-dialog-cancel"
              >
                {t("quests.daily.confirm.cancel")}
              </button>
              <button
                onClick={() => choose(confirming)}
                disabled={!!busy}
                className="library-bookmark-dialog-ok"
              >
                {busy ? t("quests.daily.confirm.busy") : t("quests.daily.confirm.ok")}
              </button>
            </div>
          </div>
        </div>
        </div>
      ), document.body)}

    </section>
  );
}
